import { useRef, useEffect, useCallback } from 'react';

const PREFIX = 'adhd-scroll-';
const DEBOUNCE = 250;

function persist(key, value) {
  try { localStorage.setItem(PREFIX + key, String(Math.round(value))); } catch {}
}

function recall(key) {
  try { return parseInt(localStorage.getItem(PREFIX + key) || '0', 10); } catch { return 0; }
}

/**
 * Persists and restores the vertical scroll position of a container.
 *
 * @param {string}         key - Unique identifier for this scroll context.
 * @param {string|number} [dep] - Optional dependency (e.g. weekOffset, date).
 *                                When it changes, the position for the new key is restored.
 * @returns {React.RefObject} scrollRef — attach to the scrollable DOM element.
 */
export function useScrollPosition(key, dep = '') {
  const scrollRef  = useRef(null);
  const timerRef   = useRef(null);
  const storageKey = dep !== '' ? `${key}-${dep}` : key;

  const onScroll = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const el = scrollRef.current;
      if (el) persist(storageKey, el.scrollTop);
    }, DEBOUNCE);
  }, [storageKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Restore after first paint so content is already laid out
    const raf = requestAnimationFrame(() => {
      const saved = recall(storageKey);
      if (saved > 0) el.scrollTop = saved;
    });

    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timerRef.current);
      if (scrollRef.current) persist(storageKey, scrollRef.current.scrollTop);
      el.removeEventListener('scroll', onScroll);
    };
  }, [storageKey, onScroll]);

  return scrollRef;
}

/**
 * Persists and restores window (page-level) scroll position.
 * Call once in the component that owns the view.
 *
 * @param {string} key       - Unique identifier.
 * @param {boolean} isActive - Only saves/restores when this view is visible.
 */
export function useWindowScrollPosition(key, isActive) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Restore
    const raf = requestAnimationFrame(() => {
      const saved = recall(key);
      if (saved > 0) window.scrollTo({ top: saved, behavior: 'instant' });
    });

    const onScroll = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => persist(key, window.scrollY), DEBOUNCE);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timerRef.current);
      persist(key, window.scrollY);
      window.removeEventListener('scroll', onScroll);
    };
  }, [key, isActive]);
}
